import { ApiResponse } from '../../shared/types';
import {
  BillPrintTicketPayload,
  CreateOutletPrinterRequest,
  CreateOutletPrinterDraft,
  FiscalReceiptPrintPayload,
  InstalledDevicePrinter,
  KotPrintTicketPayload,
  OutletPrinter,
  PrintBillTicketRequest,
  PrintFiscalReceiptRequest,
  PrintKotTicketRequest,
  PrinterDeviceConfig,
  SaveTakeAwayPrinterConfigRequest,
  SaveSectionPrinterConfigRequest,
  TakeAwayPrinterMappingView,
  SectionPrinterMappingView,
} from '../types/printer';
import { supabase } from './supabase';

const mapInstalledPrinter = (value: any): InstalledDevicePrinter => ({
  name: value?.name || '',
  description: value?.description,
  displayName: value?.displayName,
  status: value?.status,
  isDefault: Boolean(value?.isDefault),
  options: value?.options || {},
});

const mapOutletPrinter = (value: any): OutletPrinter => ({
  id: value.id,
  outlet_id: value.outlet_id,
  company_id: value.company_id,
  outlet_name: value.outlet_name,
  name: value.name,
  printer_identifier: value.printer_identifier,
  connection_type: value.connection_type,
  ip_address: value.ip_address,
  port: value.port,
  paper_size: value.paper_size,
  details: value.details || {},
  is_active: Boolean(value.is_active),
  created_by: value.created_by || null,
  created_by_name: value.created_by_name || null,
  created_at: value.created_at,
  updated_at: value.updated_at,
});

const mapSectionMapping = (value: any): SectionPrinterMappingView => ({
  id: value.id,
  outlet_id: value.outlet_id,
  company_id: value.company_id,
  section_id: value.section_id,
  section_name: value.section_name,
  operation: value.operation,
  department_id: value.department_id ?? null,
  department_name: value.department_name ?? null,
  printer_id: value.printer_id,
  printer_name: value.printer_name,
  connection_type: value.connection_type,
  printer_identifier: value.printer_identifier,
  ip_address: value.ip_address,
  port: value.port,
  paper_size: value.paper_size,
  created_at: value.created_at,
  updated_at: value.updated_at,
});

const mapTakeAwayMapping = (value: any): TakeAwayPrinterMappingView => ({
  id: value.id,
  outlet_id: value.outlet_id,
  company_id: value.company_id,
  device_id: value.device_id,
  operation: value.operation,
  department_id: value.department_id ?? null,
  department_name: value.department_name ?? null,
  printer_id: value.printer_id,
  printer_name: value.printer_name,
  connection_type: value.connection_type,
  printer_identifier: value.printer_identifier,
  ip_address: value.ip_address,
  port: value.port,
  paper_size: value.paper_size,
  created_at: value.created_at,
  updated_at: value.updated_at,
});

export const fetchCurrentDeviceId = async (): Promise<ApiResponse<string>> => {
  try {
    const response = (await window.electron.ipcRenderer.invoke(
      'printers:getDeviceId',
    )) as ApiResponse<string>;

    if (!response?.success || !response.data) {
      return {
        success: false,
        error: response?.error || 'Failed to resolve current device ID',
        data: '',
      };
    }

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to resolve current device ID',
      data: '',
    };
  }
};

export const fetchInstalledPrintersFromDevice = async (): Promise<
  ApiResponse<InstalledDevicePrinter[]>
> => {
  try {
    const response = (await window.electron.ipcRenderer.invoke(
      'printers:getInstalled',
    )) as ApiResponse<any[]>;

    if (!response?.success) {
      return {
        success: false,
        error: response?.error || 'Failed to fetch installed printers',
        data: [],
      };
    }

    const printers = (response.data || []).map(mapInstalledPrinter);

    return {
      success: true,
      data: printers,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch installed printers',
      data: [],
    };
  }
};

export const testPrinterConnection = async (
  request: CreateOutletPrinterDraft,
): Promise<ApiResponse<boolean>> => {
  try {
    const response = (await window.electron.ipcRenderer.invoke(
      'printers:testPrint',
      request,
    )) as ApiResponse<boolean>;

    if (!response?.success) {
      return {
        success: false,
        error: response?.error || 'Failed to print test page',
        data: false,
      };
    }

    return {
      success: true,
      data: true,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to print test page',
      data: false,
    };
  }
};

const mapPrinterToDeviceConfig = (printer: PrinterDeviceConfig) => ({
  name: printer.name,
  connection_type: printer.connection_type,
  printer_identifier: printer.printer_identifier,
  ip_address: printer.ip_address,
  port: printer.port,
  paper_size: printer.paper_size,
  details: printer.details || {},
});

export const printKotTicket = async (
  request: PrintKotTicketRequest,
): Promise<ApiResponse<boolean>> => {
  try {
    const response = (await window.electron.ipcRenderer.invoke(
      'printers:printKot',
      {
        printer: mapPrinterToDeviceConfig(request.printer),
        payload: request.payload as KotPrintTicketPayload,
      },
    )) as ApiResponse<boolean>;

    if (!response?.success) {
      return {
        success: false,
        error: response?.error || 'Failed to print KOT ticket',
        data: false,
      };
    }

    return {
      success: true,
      data: true,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to print KOT ticket',
      data: false,
    };
  }
};

export const printBillTicket = async (
  request: PrintBillTicketRequest,
): Promise<ApiResponse<boolean>> => {
  try {
    const response = (await window.electron.ipcRenderer.invoke(
      'printers:printBill',
      {
        printer: mapPrinterToDeviceConfig(request.printer),
        payload: request.payload as BillPrintTicketPayload,
      },
    )) as ApiResponse<boolean>;

    if (!response?.success) {
      return {
        success: false,
        error: response?.error || 'Failed to print bill ticket',
        data: false,
      };
    }

    return {
      success: true,
      data: true,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to print bill ticket',
      data: false,
    };
  }
};

// A NEW, separate print path — printBillTicket above is untouched.
export const printFiscalReceipt = async (
  request: PrintFiscalReceiptRequest,
): Promise<ApiResponse<boolean>> => {
  try {
    const response = (await window.electron.ipcRenderer.invoke(
      'printers:printFiscalReceipt',
      {
        printer: mapPrinterToDeviceConfig(request.printer),
        payload: request.payload as FiscalReceiptPrintPayload,
      },
    )) as ApiResponse<boolean>;

    if (!response?.success) {
      return {
        success: false,
        error: response?.error || 'Failed to print fiscal receipt',
        data: false,
      };
    }

    return {
      success: true,
      data: true,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to print fiscal receipt',
      data: false,
    };
  }
};

export const fetchOutletPrinters = async (
  outletId: string,
): Promise<ApiResponse<OutletPrinter[]>> => {
  try {
    const { data, error } = await supabase
      .from('outlet_printers_view')
      .select('*')
      .eq('outlet_id', outletId)
      .order('name', { ascending: true });

    if (error) {
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }

    return {
      success: true,
      data: (data || []).map(mapOutletPrinter),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch outlet printers',
      data: [],
    };
  }
};

export const createOutletPrinter = async (
  request: CreateOutletPrinterRequest,
): Promise<ApiResponse<OutletPrinter>> => {
  try {
    const { data, error } = await supabase
      .from('outlet_printers')
      .insert({
        outlet_id: request.outlet_id,
        name: request.name,
        printer_identifier: request.printer_identifier,
        connection_type: request.connection_type,
        ip_address: request.ip_address || null,
        port: request.port || null,
        paper_size: request.paper_size,
        details: request.details || {},
      })
      .select(
        'id, outlet_id, name, printer_identifier, connection_type, ip_address, port, paper_size, details, is_active, created_by, created_at, updated_at',
      )
      .single();

    if (error || !data) {
      return {
        success: false,
        error: error?.message || 'Failed to create printer',
        data: undefined,
      };
    }

    return {
      success: true,
      data: mapOutletPrinter(data),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to create printer',
      data: undefined,
    };
  }
};

export const fetchSectionPrinterMappings = async (
  outletId: string,
): Promise<ApiResponse<SectionPrinterMappingView[]>> => {
  try {
    const { data, error } = await supabase
      .from('section_printer_mappings_view')
      .select('*')
      .eq('outlet_id', outletId)
      .order('section_name', { ascending: true })
      .order('operation', { ascending: true });

    if (error) {
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }

    return {
      success: true,
      data: (data || []).map(mapSectionMapping),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch printer mappings',
      data: [],
    };
  }
};

export const fetchTakeAwayPrinterMappings = async (
  outletId: string,
  deviceId: string,
): Promise<ApiResponse<TakeAwayPrinterMappingView[]>> => {
  try {
    const { data, error } = await supabase
      .from('takeaway_printer_mappings_view')
      .select('*')
      .eq('outlet_id', outletId)
      .eq('device_id', deviceId)
      .order('operation', { ascending: true })
      .order('department_name', { ascending: true });

    if (error) {
      return {
        success: false,
        error: error.message,
        data: [],
      };
    }

    return {
      success: true,
      data: (data || []).map(mapTakeAwayMapping),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch takeaway printer mappings',
      data: [],
    };
  }
};

export const saveSectionPrinterConfiguration = async (
  request: SaveSectionPrinterConfigRequest,
): Promise<ApiResponse<boolean>> => {
  try {
    const {
      bill_printer_id: billPrinterId,
      department_mappings: departmentMappings,
      outlet_id: outletId,
      section_id: sectionId,
    } = request;

    const { error: deleteExistingError } = await supabase
      .from('section_printer_mappings')
      .delete()
      .eq('outlet_id', outletId)
      .eq('section_id', sectionId)
      .in('operation', ['KOT', 'BILL']);

    if (deleteExistingError) {
      return {
        success: false,
        error: deleteExistingError.message,
        data: false,
      };
    }

    const payload = [
      ...(billPrinterId
        ? [
            {
              outlet_id: outletId,
              section_id: sectionId,
              operation: 'BILL' as const,
              department_id: null,
              printer_id: billPrinterId,
            },
          ]
        : []),
      ...departmentMappings
        .filter((mapping) => mapping.printer_id && mapping.department_id)
        .map((mapping) => ({
          outlet_id: outletId,
          section_id: sectionId,
          operation: 'KOT' as const,
          department_id: mapping.department_id,
          printer_id: mapping.printer_id,
        })),
    ];

    if (payload.length > 0) {
      const { error: insertMappingsError } = await supabase
        .from('section_printer_mappings')
        .insert(payload);

      if (insertMappingsError) {
        return {
          success: false,
          error: insertMappingsError.message,
          data: false,
        };
      }
    }

    return {
      success: true,
      data: true,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to save section printer configuration',
      data: false,
    };
  }
};

export const saveTakeAwayPrinterConfiguration = async (
  request: SaveTakeAwayPrinterConfigRequest,
): Promise<ApiResponse<boolean>> => {
  try {
    const {
      bill_printer_id: billPrinterId,
      department_mappings: departmentMappings,
      device_id: deviceId,
      outlet_id: outletId,
    } = request;

    const { error: deleteExistingError } = await supabase
      .from('takeaway_printer_mappings')
      .delete()
      .eq('outlet_id', outletId)
      .eq('device_id', deviceId)
      .in('operation', ['KOT', 'BILL']);

    if (deleteExistingError) {
      return {
        success: false,
        error: deleteExistingError.message,
        data: false,
      };
    }

    const payload = [
      ...(billPrinterId
        ? [
            {
              outlet_id: outletId,
              device_id: deviceId,
              operation: 'BILL' as const,
              department_id: null,
              printer_id: billPrinterId,
            },
          ]
        : []),
      ...departmentMappings
        .filter((mapping) => mapping.printer_id && mapping.department_id)
        .map((mapping) => ({
          outlet_id: outletId,
          device_id: deviceId,
          operation: 'KOT' as const,
          department_id: mapping.department_id,
          printer_id: mapping.printer_id,
        })),
    ];

    if (payload.length > 0) {
      const { error: insertMappingsError } = await supabase
        .from('takeaway_printer_mappings')
        .insert(payload);

      if (insertMappingsError) {
        return {
          success: false,
          error: insertMappingsError.message,
          data: false,
        };
      }
    }

    return {
      success: true,
      data: true,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to save takeaway printer configuration',
      data: false,
    };
  }
};

export const getCachedOutletPrinters = async (
  outletId: string,
): Promise<ApiResponse<OutletPrinter[]>> => {
  try {
    const response = (await window.electron.ipcRenderer.invoke(
      'printers:cache:getOutletPrinters',
      outletId,
    )) as ApiResponse<any[]>;

    if (!response?.success) {
      return {
        success: false,
        error: response?.error || 'Failed to read cached outlet printers',
        data: [],
      };
    }

    return {
      success: true,
      data: (response.data || []).map(mapOutletPrinter),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to read cached outlet printers',
      data: [],
    };
  }
};

export const setCachedOutletPrinters = async (
  outletId: string,
  printers: OutletPrinter[],
): Promise<ApiResponse<boolean>> => {
  try {
    const response = (await window.electron.ipcRenderer.invoke(
      'printers:cache:setOutletPrinters',
      outletId,
      printers,
    )) as ApiResponse<boolean>;

    if (!response?.success) {
      return {
        success: false,
        error: response?.error || 'Failed to cache outlet printers',
        data: false,
      };
    }

    return {
      success: true,
      data: true,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to cache outlet printers',
      data: false,
    };
  }
};

export const getCachedSectionMappings = async (
  outletId: string,
): Promise<ApiResponse<SectionPrinterMappingView[]>> => {
  try {
    const response = (await window.electron.ipcRenderer.invoke(
      'printers:cache:getSectionMappings',
      outletId,
    )) as ApiResponse<any[]>;

    if (!response?.success) {
      return {
        success: false,
        error:
          response?.error || 'Failed to read cached section printer mappings',
        data: [],
      };
    }

    const data = Array.isArray(response.data) ? response.data : [];

    return {
      success: true,
      data: data.map(mapSectionMapping),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to read cached section printer mappings',
      data: [],
    };
  }
};

export const setCachedSectionMappings = async (
  outletId: string,
  mappings: SectionPrinterMappingView[],
): Promise<ApiResponse<boolean>> => {
  try {
    const response = (await window.electron.ipcRenderer.invoke(
      'printers:cache:setSectionMappings',
      outletId,
      mappings,
    )) as ApiResponse<boolean>;

    if (!response?.success) {
      return {
        success: false,
        error: response?.error || 'Failed to cache section printer mappings',
        data: false,
      };
    }

    return {
      success: true,
      data: true,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to cache section printer mappings',
      data: false,
    };
  }
};

export const getCachedTakeAwayMappings = async (
  outletId: string,
  deviceId: string,
): Promise<ApiResponse<TakeAwayPrinterMappingView[]>> => {
  try {
    const response = (await window.electron.ipcRenderer.invoke(
      'printers:cache:getTakeAwayMappings',
      outletId,
      deviceId,
    )) as ApiResponse<any[]>;

    if (!response?.success) {
      return {
        success: false,
        error:
          response?.error || 'Failed to read cached takeaway printer mappings',
        data: [],
      };
    }

    const data = Array.isArray(response.data) ? response.data : [];

    return {
      success: true,
      data: data.map(mapTakeAwayMapping),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to read cached takeaway printer mappings',
      data: [],
    };
  }
};

export const setCachedTakeAwayMappings = async (
  outletId: string,
  deviceId: string,
  mappings: TakeAwayPrinterMappingView[],
): Promise<ApiResponse<boolean>> => {
  try {
    const response = (await window.electron.ipcRenderer.invoke(
      'printers:cache:setTakeAwayMappings',
      outletId,
      deviceId,
      mappings,
    )) as ApiResponse<boolean>;

    if (!response?.success) {
      return {
        success: false,
        error: response?.error || 'Failed to cache takeaway printer mappings',
        data: false,
      };
    }

    return {
      success: true,
      data: true,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to cache takeaway printer mappings',
      data: false,
    };
  }
};
